import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import moment from "moment";
import {
  Search, X, Stethoscope, User, CalendarRange, Info, Download,
  CalendarDays, Pencil, FlaskConical, Receipt, MessageSquare, PhoneCall, History,
} from "lucide-react";
import { getActivityLog, getDoctors, getPatients } from "../../api/api";
import SegmentedControl from "../../components/SegmentedControl";
import FilterMultiSelect from "../../components/FilterMultiSelect";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "../../components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import { downloadXlsx } from "../../lib/xlsx";
import { cn } from "../../lib/utils";

const ISO = "YYYY-MM-DD";

const SUBJECTS = [
  { value: "doctor", label: "By doctor" },
  { value: "patient", label: "By patient" },
];

const DATE_PRESETS = [
  { value: "today", label: "Today", range: () => [moment().format(ISO), moment().format(ISO)] },
  { value: "yesterday", label: "Yesterday", range: () => [moment().subtract(1, "day").format(ISO), moment().subtract(1, "day").format(ISO)] },
  { value: "7d", label: "Last 7d", range: () => [moment().subtract(6, "days").format(ISO), moment().format(ISO)] },
  { value: "30d", label: "Last 30d", range: () => [moment().subtract(29, "days").format(ISO), moment().format(ISO)] },
  { value: "all", label: "All time", range: () => ["", ""] },
  { value: "custom", label: "Custom", range: null },
];

const EVENT_META = {
  appointment: { label: "Appointments", Icon: CalendarDays, tone: "text-secondary", bg: "bg-secondary/10" },
  appointment_edit: { label: "Edits", Icon: Pencil, tone: "text-warning", bg: "bg-warning/10" },
  lab_order: { label: "Lab orders", Icon: FlaskConical, tone: "text-primary", bg: "bg-primary/10" },
  invoice: { label: "Invoices", Icon: Receipt, tone: "text-secondary", bg: "bg-secondary/10" },
  message: { label: "Messages", Icon: MessageSquare, tone: "text-muted-foreground", bg: "bg-muted" },
  call: { label: "Calls", Icon: PhoneCall, tone: "text-muted-foreground", bg: "bg-muted" },
};

const TYPE_OPTIONS = Object.entries(EVENT_META).map(([value, m]) => ({ value, label: m.label }));

const presetFor = (from, to) => {
  if (!from && !to) return "all";
  const m = DATE_PRESETS.find((p) => p.range && p.range()[0] === from && p.range()[1] === to);
  return m ? m.value : "custom";
};

/** Searchable picker shared by the doctor and patient modes. */
function SubjectPicker({ mode, value, onChange }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      const fetcher = mode === "doctor" ? getDoctors : getPatients;
      fetcher({ search: q, limit: 8 })
        .then((res) => setResults(res.data || []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, mode]);

  const Icon = mode === "doctor" ? Stethoscope : User;

  return (
    <DropdownMenu onOpenChange={(o) => !o && setQuery("")}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn("h-9 w-full justify-between gap-2 bg-card px-3 font-normal",
            !value && "text-muted-foreground", value && "border-primary/40 bg-primary/5")}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">{value ? value.name : `Choose a ${mode}`}</span>
          </span>
          {value && (
            <span role="button" tabIndex={-1} aria-label="Clear"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(null); }}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <X className="h-3.5 w-3.5" />
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[18rem] p-0">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder={mode === "doctor" ? "Search doctors…" : "Search by name or mobile…"}
              className="h-8 pl-8 text-sm" />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto p-1">
          {loading ? (
            <div className="space-y-1 p-1"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /></div>
          ) : query.trim().length < 2 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">Type at least two characters.</p>
          ) : results.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">No {mode} found.</p>
          ) : (
            results.map((r) => (
              <DropdownMenuItem key={r._id} onSelect={() => onChange(r)} className="flex-col items-start gap-0 py-2">
                <span className="font-medium">{r.name}</span>
                <span className="text-xs text-muted-foreground">{r.specialty || r.phone}</span>
              </DropdownMenuItem>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ActivityLog() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState(() => searchParams.get("mode") || "doctor");
  const [subject, setSubject] = useState(null);
  const [from, setFrom] = useState(() => searchParams.get("from") || moment().format(ISO));
  const [to, setTo] = useState(() => searchParams.get("to") || moment().format(ISO));
  const [types, setTypes] = useState([]);
  // Explicit, because a custom range can coincide with a preset.
  const [customMode, setCustomMode] = useState(() => searchParams.get("custom") === "1");

  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({});
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Re-hydrate the chosen subject from the URL so a shared link works.
  useEffect(() => {
    const id = searchParams.get("id");
    if (!id || subject) return;
    const fetcher = mode === "doctor" ? getDoctors : getPatients;
    fetcher({ limit: 500 })
      .then((res) => setSubject((res.data || []).find((r) => r._id === id) || null))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const n = new URLSearchParams();
    n.set("mode", mode);
    if (subject) n.set("id", subject._id);
    if (from) n.set("from", from);
    if (to) n.set("to", to);
    if (customMode) n.set("custom", "1");
    if (n.toString() !== searchParams.toString()) setSearchParams(n, { replace: true });
  }, [mode, subject, from, to, customMode, searchParams, setSearchParams]);

  const load = useCallback(() => {
    if (!subject) { setEvents([]); setCounts({}); setNotes([]); return; }
    setLoading(true);
    getActivityLog({
      [mode === "doctor" ? "doctor_id" : "patient_id"]: subject._id,
      from: from || undefined,
      to: to || undefined,
      types: types.length ? types.join(",") : undefined,
    })
      .then((res) => {
        setEvents(res.data || []);
        setCounts(res.counts || {});
        setNotes(res.meta?.notes || []);
      })
      .catch((err) => toast.error(err?.message || "Could not load the activity log."))
      .finally(() => setLoading(false));
  }, [subject, mode, from, to, types]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const derivedPreset = presetFor(from, to);
  const preset = customMode || derivedPreset === "custom" ? "custom" : derivedPreset;

  const applyPreset = (key) => {
    if (key === "custom") {
      setCustomMode(true);
      // Seed with today so the range is valid the moment the inputs appear.
      if (!from && !to) {
        const today = moment().format(ISO);
        setFrom(today);
        setTo(today);
      }
      return;
    }
    setCustomMode(false);
    const chosen = DATE_PRESETS.find((p) => p.value === key);
    if (!chosen?.range) return;
    const [f, t] = chosen.range();
    setFrom(f);
    setTo(t);
  };

  // Group by calendar day so a multi-day range still reads as a diary.
  const grouped = useMemo(() => {
    const byDay = new Map();
    for (const e of events) {
      const day = e.at ? moment(e.at).format(ISO) : e.occurred_on || "—";
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(e);
    }
    return [...byDay.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [events]);

  const handleExport = () => {
    if (!events.length) { toast("Nothing to export."); return; }
    const headers = ["Date", "Time", "Type", "Title", "Details", "Patient", "Doctor", "Actor", "Status", "Amount"];
    const rows = events.map((e) => [
      e.at ? moment(e.at).format(ISO) : e.occurred_on || "-",
      e.at ? moment(e.at).format("HH:mm") : "-",
      EVENT_META[e.type]?.label || e.type,
      e.title || "-",
      e.description || "-",
      e.patient?.name || "-",
      e.doctor?.name || "-",
      e.actor || "-",
      e.status || "-",
      e.amount || 0,
    ]);
    downloadXlsx(
      `activity_${subject.name.replace(/\s+/g, "_")}_${from || "all"}.xlsx`,
      headers, rows,
      { sheetName: "Activity Log", widths: [12, 8, 16, 42, 52, 22, 22, 18, 16, 11] }
    );
    toast.success(`Exported ${rows.length} events.`);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold">Activity Log</h2>
          <p className="text-sm text-muted-foreground">
            Everything that happened for one doctor or patient, in order
          </p>
        </div>
        {events.length > 0 && (
          <Button variant="outline" className="bg-card" onClick={handleExport}>
            <Download className="h-4 w-4" /> Export
          </Button>
        )}
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SegmentedControl
            items={SUBJECTS}
            value={mode}
            onChange={(v) => { setMode(v); setSubject(null); }}
            size="sm"
          />
          <div className="sm:w-72">
            <SubjectPicker mode={mode} value={subject} onChange={setSubject} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl items={DATE_PRESETS} value={preset} onChange={applyPreset} size="sm" />
          <span className="mx-1 hidden h-5 w-px bg-border sm:block" aria-hidden="true" />
          <FilterMultiSelect label="Event types" options={TYPE_OPTIONS} value={types} onChange={setTypes} />
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 p-2.5">
            <CalendarRange className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <label htmlFor="al-from" className="text-xs font-medium text-muted-foreground">From</label>
            <Input id="al-from" type="date" value={from} max={to || undefined}
              onChange={(e) => setFrom(e.target.value)} className="h-8 w-[10rem] bg-card text-sm" />
            <label htmlFor="al-to" className="text-xs font-medium text-muted-foreground">To</label>
            <Input id="al-to" type="date" value={to} min={from || undefined}
              onChange={(e) => setTo(e.target.value)} className="h-8 w-[10rem] bg-card text-sm" />
          </div>
        )}
      </div>

      {/* summary */}
      {subject && !loading && (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1.5">
            {mode === "doctor" ? <Stethoscope className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {subject.name}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {counts.all ?? 0} event{counts.all === 1 ? "" : "s"}
            {from && to ? ` · ${from === to ? moment(from, ISO).format("D MMM YYYY") : `${from} → ${to}`}` : " · all time"}
          </span>
          {Object.entries(EVENT_META).map(([type, m]) =>
            counts[type] ? (
              <Badge key={type} variant="outline" className="gap-1 font-normal">
                <m.Icon className={cn("h-3 w-3", m.tone)} /> {counts[type]}
              </Badge>
            ) : null
          )}
        </div>
      )}

      {notes.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="space-y-0.5">{notes.map((n, i) => <p key={i}>{n}</p>)}</div>
        </div>
      )}

      {/* timeline */}
      {!subject ? (
        <div className="rounded-lg border border-dashed border-border bg-card px-4 py-16 text-center">
          <History className="mx-auto h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm font-medium">Choose a {mode} to begin</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick someone above and set a date range to see their complete log.
          </p>
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card px-4 py-16 text-center">
          <p className="text-sm text-muted-foreground">
            No activity for {subject.name}
            {from && to ? ` between ${from} and ${to}` : ""}.
          </p>
          <Button variant="outline" size="sm" className="mt-3 bg-card" onClick={() => applyPreset("all")}>
            Search all time instead
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(([day, dayEvents]) => (
            <div key={day} className="rounded-lg border border-border bg-card">
              <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2">
                <p className="text-sm font-medium">
                  {day === "—" ? "Undated" : moment(day, ISO).format("dddd, D MMMM YYYY")}
                </p>
                <span className="text-xs text-muted-foreground">
                  {dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}
                </span>
              </div>

              <ol className="divide-y divide-border">
                {dayEvents.map((e) => {
                  const meta = EVENT_META[e.type] || EVENT_META.message;
                  const Icon = meta.Icon;
                  return (
                    <li key={e.id} className="flex gap-3 px-4 py-3">
                      <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full", meta.bg)}>
                        <Icon className={cn("h-3.5 w-3.5", meta.tone)} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <p className="text-sm font-medium">{e.title}</p>
                          {e.slot_time && (
                            <span className="text-xs text-muted-foreground">{e.slot_time}</span>
                          )}
                        </div>
                        {e.description && (
                          <p className="mt-0.5 break-words text-sm text-muted-foreground">{e.description}</p>
                        )}
                        <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          {/* Show the counterparty, not the person you filtered by */}
                          {mode === "doctor" && e.patient?.name && <span>Patient: {e.patient.name}</span>}
                          {mode === "patient" && e.doctor?.name && <span>Doctor: {e.doctor.name}</span>}
                          {e.actor && <span>· by {e.actor}</span>}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {e.at ? moment(e.at).format("HH:mm") : ""}
                      </span>
                    </li>
                  );
                })}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
